import { CalendarCacheStoreData, addToCalendarsCache } from 'owa-calendar-cache';

import type FolderId from 'owa-service/lib/contract/FolderId';
import type { LocalCalendarEntry } from 'owa-graph-schema';
import { getCalendarColorScheme } from 'owa-calendar-color-resolver';
import { getCalendarServiceInCacheFormat } from '../services/getCalendarServiceInCacheFormat';
import { selectCalendar } from './selectCalendar';

/**
 * Reloads calendars cache and selects given calendar
 * @param addedCalendarFolderId calendar to be added in the cache
 */
export default function reloadCacheAndSelectCalendar(
    addedCalendarFolderId: FolderId
): Promise<boolean> {
    return reloadCacheAndSelectCalendars([addedCalendarFolderId]);
}

/**
 * Reloads calendars cache and selects given list of calendars
 * @param addedCalendarFolderIds List of calendar to be added in the cache
 * @param prefetchedCacheStoreData If the store data has already been fetched
 * then pass it to use that data so we don't need to go back to server
 * @returns true if any calendars were added, else false
 */
export async function reloadCacheAndSelectCalendars(
    addedCalendarFolderIds: FolderId[],
    prefetchedCacheStoreData?: CalendarCacheStoreData
) {
    // 1. Reloading calendars cache.
    // At this point, we do not have correct calendar folder entries and hence this approach.
    const cacheDataResponse = prefetchedCacheStoreData || (await getCalendarServiceInCacheFormat());
    if (cacheDataResponse == null) {
        return false;
    }

    let calendarsAdded: boolean = false;
    for (const addedCalendarFolderId of addedCalendarFolderIds) {
        // 2. Populate the newly added calendar object by inspecting the cacheData of getCalendarsService
        // After which, add this new calendar entry to the calendars cache and add it to calendars collection in store.
        const calendarId = cacheDataResponse.folderIdMapping[addedCalendarFolderId.Id];
        const calendarEntry: LocalCalendarEntry = cacheDataResponse.calendarEntryMapping[
            calendarId.id
        ] as LocalCalendarEntry;

        addToCalendarsCache(calendarEntry);

        // assign color scheme to the newly added calendar
        calendarEntry.colorScheme = getCalendarColorScheme(calendarEntry.calendarId);

        // 3. Make the selectcalendar action call to retrieve items for the calendar.
        selectCalendar(calendarEntry);
        calendarsAdded = true;
    }

    return calendarsAdded;
}
